import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Search, Plus, Send, Smile, Edit3, Check, XCircle,
  Archive, ChevronLeft, MoreVertical, Loader2, UserCircle2
} from 'lucide-react';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';

const EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👎'];

export default function ChatModal({ open, onClose }) {
  const { socket, presenceMap } = useSocket();
  const { user } = useAuth();
  const myUserId = user?.userId;

  // UI state
  const [view, setView] = useState('list'); // list | chat
  const [tab, setTab] = useState('Active');  // Active | Closed
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  // Compose
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(null); // messageId or 'input'
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Edit
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');

  // New conversation
  const [showNew, setShowNew] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [newParticipants, setNewParticipants] = useState([]);
  const [userSearch, setUserSearch] = useState('');

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  // Typing
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimerRef = useRef(null);

  // Close menu
  const [showConvMenu, setShowConvMenu] = useState(false);

  // ─── Load conversations ───
  const loadConversations = useCallback(async (status = tab) => {
    setLoadingConvs(true);
    try {
      const res = await apiClient.get('/chat/conversations', { params: { status } });
      setConversations(res.data);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoadingConvs(false);
    }
  }, [tab]);

  useEffect(() => {
    if (open) loadConversations();
  }, [open, tab, loadConversations]);

  // Auto-refresh conversations every 10s when chat is open
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => loadConversations(), 10000);
    return () => clearInterval(interval);
  }, [open, tab, loadConversations]);

  // ─── Load messages for active conversation ───
  const loadMessages = useCallback(async (convId, before) => {
    setLoadingMsgs(true);
    try {
      const params = { pageSize: 50 };
      if (before) params.before = before;
      const res = await apiClient.get(`/chat/conversations/${convId}/messages`, { params });
      if (before) {
        setMessages(prev => [...res.data.messages, ...prev]);
      } else {
        setMessages(res.data.messages);
      }
      setHasMore(res.data.hasMore);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  // ─── Open a conversation ───
  function openConversation(conv) {
    setActiveConv(conv);
    setView('chat');
    setMessages([]);
    loadMessages(conv.ConversationID);
    // Mark as read
    socket?.emit('chat:read', { conversationId: conv.ConversationID });
  }

  // ─── Scroll to bottom ───
  useEffect(() => {
    if (view === 'chat' && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, view]);

  // ─── Socket listeners ───
  useEffect(() => {
    if (!socket) return;

    function onMessage(msg) {
      // If we're viewing this conversation, append the message live.
      // Gate on activeConv (not prev[0]) so it works even when the message
      // list is empty (first message in a new conversation).
      if (activeConv?.ConversationID === msg.ConversationID) {
        setMessages(prev => {
          if (prev.some(m => m.MessageID === msg.MessageID)) return prev;
          return [...prev, msg];
        });
        if (msg.SenderUserID !== myUserId) {
          socket.emit('chat:read', { conversationId: msg.ConversationID });
        }
      }
      // Refresh conversation list for unread counts / last-message preview
      loadConversations();
    }

    function onEdited({ messageId, content, isEdited, editedAt }) {
      setMessages(prev => prev.map(m =>
        m.MessageID === messageId ? { ...m, Content: content, IsEdited: isEdited, EditedAt: editedAt } : m
      ));
    }

    function onReactions({ messageId, reactions }) {
      setMessages(prev => prev.map(m =>
        m.MessageID === messageId ? { ...m, reactions } : m
      ));
    }

    function onClosed({ conversationId }) {
      setConversations(prev => prev.filter(c => c.ConversationID !== conversationId));
      if (activeConv?.ConversationID === conversationId) {
        setActiveConv(prev => prev ? { ...prev, Status: 'Closed' } : prev);
      }
    }

    function onTyping({ conversationId, userId, isTyping }) {
      if (userId === myUserId) return;
      setTypingUsers(prev => {
        const convTyping = { ...(prev[conversationId] || {}) };
        if (isTyping) convTyping[userId] = true;
        else delete convTyping[userId];
        return { ...prev, [conversationId]: convTyping };
      });
    }

    socket.on('chat:message', onMessage);
    socket.on('chat:edited', onEdited);
    socket.on('chat:reactions', onReactions);
    socket.on('chat:closed', onClosed);
    socket.on('chat:typing', onTyping);

    return () => {
      socket.off('chat:message', onMessage);
      socket.off('chat:edited', onEdited);
      socket.off('chat:reactions', onReactions);
      socket.off('chat:closed', onClosed);
      socket.off('chat:typing', onTyping);
    };
  }, [socket, activeConv, myUserId, loadConversations]);

  // ─── Send message ───
  function handleSend() {
    if (!draft.trim() || !activeConv || sending) return;
    setSending(true);
    socket?.emit('chat:send', {
      conversationId: activeConv.ConversationID,
      content: draft.trim(),
    }, (res) => {
      setSending(false);
      if (res?.success) {
        setDraft('');
        inputRef.current?.focus();
      }
    });
    // Stop typing indicator
    socket?.emit('chat:typing', { conversationId: activeConv.ConversationID, isTyping: false });
  }

  // ─── Typing indicator ───
  function handleInputChange(val) {
    setDraft(val);
    if (!activeConv) return;
    socket?.emit('chat:typing', { conversationId: activeConv.ConversationID, isTyping: true });
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      socket?.emit('chat:typing', { conversationId: activeConv.ConversationID, isTyping: false });
    }, 2000);
  }

  // ─── Edit message ───
  async function handleEdit(messageId) {
    if (!editContent.trim()) return;
    try {
      await apiClient.put(`/chat/messages/${messageId}`, { content: editContent.trim() });
      setEditingId(null);
      setEditContent('');
    } catch (err) {
      console.error('Edit failed:', err);
    }
  }

  // ─── React to message ───
  function handleReaction(messageId, emoji) {
    socket?.emit('chat:react', { messageId, emoji }, () => {});
    setShowEmoji(null);
  }

  // ─── Close conversation ───
  async function handleCloseConversation() {
    if (!activeConv) return;
    try {
      await apiClient.post(`/chat/conversations/${activeConv.ConversationID}/close`);
      setShowConvMenu(false);
      setView('list');
      setActiveConv(null);
      loadConversations();
    } catch (err) {
      console.error('Close conversation failed:', err);
    }
  }

  // ─── New conversation ───
  async function loadCompanyUsers() {
    try {
      const res = await apiClient.get('/chat/users');
      setCompanyUsers(res.data);
    } catch { /* ignore */ }
  }

  async function handleCreateConversation() {
    if (!newSubject.trim() || !newParticipants.length) return;
    try {
      const res = await apiClient.post('/chat/conversations', {
        subject: newSubject.trim(),
        participantUserIds: newParticipants,
      });
      setShowNew(false);
      setNewSubject('');
      setNewParticipants([]);
      setUserSearch('');
      await loadConversations();
      // Open the new conversation
      openConversation(res.data);
      // Join socket room
      socket?.emit('chat:read', { conversationId: res.data.ConversationID });
    } catch (err) {
      console.error('Create conversation failed:', err);
    }
  }

  // ─── Search ───
  async function handleSearch(q) {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults(null); return; }
    try {
      const res = await apiClient.get('/chat/conversations/search', { params: { q: q.trim() } });
      setSearchResults(res.data);
    } catch { setSearchResults([]); }
  }

  // ─── Presence dot ───
  function PresenceDot({ userId, className = '' }) {
    const status = presenceMap[userId];
    return (
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${status === 'online' ? 'bg-green-500' : 'bg-gray-300'} ${className}`} />
    );
  }

  if (!open) return null;

  const convTyping = activeConv ? typingUsers[activeConv.ConversationID] : null;
  const typingNames = convTyping ? Object.keys(convTyping) : [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl flex overflow-hidden"
        style={{ width: '820px', height: '600px', maxWidth: '95vw', maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ─── LEFT PANEL: Conversation List ─── */}
        <div className={`w-80 border-r border-gray-200 flex flex-col bg-gray-50 ${view === 'chat' ? 'hidden md:flex' : 'flex'}`}>
          {/* Header */}
          <div className="p-4 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-900">Chat</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setShowNew(true); loadCompanyUsers(); }}
                  className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="New conversation"
                >
                  <Plus className="h-5 w-5" />
                </button>
                <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
              />
            </div>

            {/* Tabs */}
            <div className="flex mt-3 bg-gray-100 rounded-lg p-0.5">
              {['Active', 'Closed'].map(t => (
                <button
                  key={t}
                  onClick={() => { setTab(t); setSearchResults(null); setSearchQuery(''); }}
                  className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
                    tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t === 'Closed' ? 'Archived' : t}
                </button>
              ))}
            </div>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {loadingConvs ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : (searchResults || conversations).length === 0 ? (
              <div className="text-center text-sm text-gray-400 py-12">
                {searchResults ? 'No results found' : tab === 'Active' ? 'No conversations yet' : 'No archived conversations'}
              </div>
            ) : (
              (searchResults || conversations).map(conv => {
                const participantIds = conv.OtherParticipantIDs ? conv.OtherParticipantIDs.split(',') : [];
                const anyOnline = participantIds.some(id => presenceMap[id] === 'online');
                return (
                <button
                  key={conv.ConversationID}
                  onClick={() => openConversation(conv)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-white transition-colors ${
                    activeConv?.ConversationID === conv.ConversationID ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2.5 flex-1 min-w-0">
                      <div className="relative mt-1 shrink-0">
                        <UserCircle2 className="h-8 w-8 text-gray-300" />
                        <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${
                          anyOnline ? 'bg-green-500' : 'bg-gray-300'
                        }`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{conv.Subject}</p>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{conv.OtherParticipants || 'Just you'}</p>
                        {conv.LastMessage && (
                          <p className="text-xs text-gray-400 truncate mt-1">
                            {conv.LastMessageSender ? `${conv.LastMessageSender.split(' ')[0]}: ` : ''}
                            {conv.LastMessage}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end ml-2 shrink-0">
                      <span className="text-[10px] text-gray-400">
                        {conv.LastMessageAt
                          ? new Date(conv.LastMessageAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                          : ''}
                      </span>
                      {conv.UnreadCount > 0 && (
                        <span className="mt-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-500 px-1.5 text-[10px] font-bold text-white">
                          {conv.UnreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
                );
              })
            )}
          </div>
        </div>

        {/* ─── RIGHT PANEL: Messages ─── */}
        <div className={`flex-1 flex flex-col ${view === 'list' ? 'hidden md:flex' : 'flex'}`}>
          {!activeConv ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              Select a conversation or start a new one
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-3 min-w-0">
                  <button onClick={() => setView('list')} className="md:hidden p-1 text-gray-400 hover:text-gray-600">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-gray-900 truncate">{activeConv.Subject}</h3>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {(activeConv.OtherParticipantIDs || '').split(',').filter(Boolean).map(id => (
                        <span key={id} className={`inline-block h-2 w-2 rounded-full ${presenceMap[id] === 'online' ? 'bg-green-500' : 'bg-gray-300'}`} title={presenceMap[id] === 'online' ? 'Online' : 'Offline'} />
                      ))}
                      <p className="text-xs text-gray-500 truncate">{activeConv.OtherParticipants || 'Just you'}</p>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowConvMenu(!showConvMenu)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                  {showConvMenu && (
                    <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 w-44">
                      {activeConv.Status === 'Active' && (
                        <button
                          onClick={handleCloseConversation}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Archive className="h-4 w-4" /> Close & Archive
                        </button>
                      )}
                      <button
                        onClick={() => { setShowConvMenu(false); onClose(); }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <X className="h-4 w-4" /> Close Chat
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Messages area */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1 bg-gray-50">
                {hasMore && (
                  <button
                    onClick={() => messages.length > 0 && loadMessages(activeConv.ConversationID, messages[0].CreatedAt)}
                    disabled={loadingMsgs}
                    className="w-full text-center text-xs text-blue-500 hover:text-blue-700 py-2"
                  >
                    {loadingMsgs ? 'Loading...' : 'Load older messages'}
                  </button>
                )}

                {loadingMsgs && messages.length === 0 && (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                )}

                {messages.map((msg, i) => {
                  const isMe = msg.SenderUserID === myUserId;
                  const showSender = !isMe && (i === 0 || messages[i - 1]?.SenderUserID !== msg.SenderUserID);
                  const reactions = msg.reactions || [];

                  return (
                    <div key={msg.MessageID} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
                        {showSender && (
                          <div className="flex items-center gap-1.5 mb-0.5 ml-1">
                            <PresenceDot userId={msg.SenderUserID} />
                            <span className="text-xs font-medium text-gray-500">{msg.SenderName}</span>
                          </div>
                        )}

                        <div className="group relative">
                          {editingId === msg.MessageID ? (
                            <div className="flex items-center gap-1">
                              <input
                                value={editContent}
                                onChange={e => setEditContent(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleEdit(msg.MessageID)}
                                className="flex-1 text-sm px-3 py-1.5 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                              <button onClick={() => handleEdit(msg.MessageID)} className="p-1 text-green-600 hover:text-green-800">
                                <Check className="h-4 w-4" />
                              </button>
                              <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600">
                                <XCircle className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div
                              className={`px-3 py-2 rounded-2xl text-sm ${
                                isMe
                                  ? 'bg-blue-500 text-white rounded-br-md'
                                  : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
                              }`}
                            >
                              <p className="whitespace-pre-wrap break-words">{msg.Content}</p>
                              {msg.IsEdited && (
                                <span className={`text-[10px] ${isMe ? 'text-blue-200' : 'text-gray-400'}`}> (edited)</span>
                              )}
                            </div>
                          )}

                          {/* Hover actions */}
                          {editingId !== msg.MessageID && (
                            <div className={`absolute ${isMe ? 'left-0 -translate-x-full' : 'right-0 translate-x-full'} top-0 hidden group-hover:flex items-center gap-0.5 px-1`}>
                              <button
                                onClick={() => setShowEmoji(showEmoji === msg.MessageID ? null : msg.MessageID)}
                                className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                title="React"
                              >
                                <Smile className="h-3.5 w-3.5" />
                              </button>
                              {isMe && (
                                <button
                                  onClick={() => { setEditingId(msg.MessageID); setEditContent(msg.Content); }}
                                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                  title="Edit"
                                >
                                  <Edit3 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          )}

                          {/* Emoji picker */}
                          {showEmoji === msg.MessageID && (
                            <div className={`absolute ${isMe ? 'right-0' : 'left-0'} -top-9 bg-white rounded-lg shadow-lg border border-gray-200 px-1.5 py-1 flex gap-0.5 z-10`}>
                              {EMOJIS.map(e => (
                                <button
                                  key={e}
                                  onClick={() => handleReaction(msg.MessageID, e)}
                                  className="hover:bg-gray-100 rounded p-0.5 text-base"
                                >
                                  {e}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Reactions */}
                        {reactions.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-0.5 ml-1">
                            {Object.entries(reactions.reduce((acc, r) => {
                              acc[r.Emoji] = acc[r.Emoji] || [];
                              acc[r.Emoji].push(r.UserName);
                              return acc;
                            }, {})).map(([emoji, users]) => (
                              <span
                                key={emoji}
                                className="inline-flex items-center text-xs bg-gray-100 rounded-full px-1.5 py-0.5 cursor-default"
                                title={users.join(', ')}
                              >
                                {emoji} {users.length > 1 && users.length}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Timestamp */}
                        <p className={`text-[10px] text-gray-400 mt-0.5 ${isMe ? 'text-right mr-1' : 'ml-1'}`}>
                          {new Date(msg.CreatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}

                {/* Typing indicator */}
                {typingNames.length > 0 && (
                  <div className="text-xs text-gray-400 italic pl-2">Someone is typing...</div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input area */}
              {activeConv.Status === 'Active' ? (
                <div className="px-4 py-3 border-t border-gray-200 bg-white">
                  <div className="flex items-end gap-2">
                    <div className="flex-1 relative">
                      <textarea
                        ref={inputRef}
                        value={draft}
                        onChange={e => handleInputChange(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                        }}
                        placeholder="Type a message..."
                        rows={1}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        style={{ minHeight: '38px', maxHeight: '100px' }}
                      />
                    </div>
                    <button
                      onClick={handleSend}
                      disabled={!draft.trim() || sending}
                      className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-center text-sm text-gray-400">
                  <Archive className="h-4 w-4 inline mr-1" /> This conversation has been archived
                </div>
              )}
            </>
          )}
        </div>

        {/* ─── New Conversation Modal ─── */}
        {showNew && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30" onClick={() => setShowNew(false)}>
            <div className="bg-white rounded-xl shadow-xl w-96 max-w-[90vw] p-5" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-gray-900 mb-4">New Conversation</h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={newSubject}
                    onChange={e => setNewSubject(e.target.value)}
                    placeholder="What's this about?"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Participants</label>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-2"
                  />

                  {/* Selected participants */}
                  {newParticipants.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {newParticipants.map(pid => {
                        const u = companyUsers.find(x => x.UserID === pid);
                        return (
                          <span key={pid} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                            {u?.DisplayName || 'User'}
                            <button onClick={() => setNewParticipants(prev => prev.filter(id => id !== pid))} className="hover:text-blue-600">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                    {companyUsers
                      .filter(u => {
                        const name = (u.DisplayName || '').toLowerCase();
                        const email = (u.Email || '').toLowerCase();
                        const q = userSearch.toLowerCase();
                        return (!q || name.includes(q) || email.includes(q)) && !newParticipants.includes(u.UserID);
                      })
                      .map(u => (
                        <button
                          key={u.UserID}
                          onClick={() => setNewParticipants(prev => [...prev, u.UserID])}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <PresenceDot userId={u.UserID} />
                            <span>{u.DisplayName}</span>
                          </div>
                          <span className="text-xs text-gray-400">{u.Email}</span>
                        </button>
                      ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setShowNew(false)} className="btn-secondary text-sm px-4 py-2">Cancel</button>
                <button
                  onClick={handleCreateConversation}
                  disabled={!newSubject.trim() || !newParticipants.length}
                  className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
                >
                  Start Chat
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
